import { Text, Group, ActionIcon, Box, Paper, Stack, Card, Tooltip, Title } from '@mantine/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useMediaQuery } from '@mantine/hooks'
import { getShiftPosition, getTimeSlots, ShiftDayWindow } from './shiftTimelineHelpers'
import { ExtendedShift, ExtendedVehicleShifts } from '@/hooks/useShiftsData'
import { TbTrash } from 'react-icons/tb'
import { DriverNameDisplay } from './DriverNameDisplay'
import { getTimezone } from '@/utils/datetime'

// Configure dayjs plugins
dayjs.extend(utc)
dayjs.extend(timezone)

type TimeSlot = ReturnType<typeof getTimeSlots>[number]

function TimeGrid({ timeSlots, isContinuation }: { timeSlots: TimeSlot[]; isContinuation?: boolean }) {
  return (
    <>
      {timeSlots.map((timeSlot, index) =>
        isContinuation && index === 0 ? null : (
          <Box
            key={timeSlot.label + index}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: timeSlot.top,
              height: '1px',
              backgroundColor: 'var(--mantine-color-gray-3)',
              zIndex: 1,
            }}
          />
        )
      )}
    </>
  )
}

// Reusable component for time labels
function TimeLabels({
  timeSlots,
  isMobile,
  isContinuation,
}: {
  timeSlots: TimeSlot[]
  isMobile: boolean
  isContinuation?: boolean
}) {
  return (
    <>
      {timeSlots.map((timeSlot, index) =>
        isContinuation && index === 0 ? null : (
          <Box
            key={timeSlot.label + index}
            style={{
              position: 'absolute',
              left: isMobile ? '-50px' : '-70px',
              top: timeSlot.top,
              transform: 'translateY(-50%)',
              fontSize: isMobile ? '12px' : '14px',
              color: 'light-dark(var(--mantine-color-gray-7), #c9c9c9)',
              fontWeight: 600,
              zIndex: 2,
              minWidth: isMobile ? '40px' : '50px',
              textAlign: 'right',
            }}
          >
            {timeSlot.label}
          </Box>
        )
      )}
    </>
  )
}

// Reusable component for a single shift
function ShiftItem({
  shift,
  position,
  onDeleteShift,
  isDeleting,
  isMobile,
  isEditable,
}: {
  shift: ExtendedShift
  position: { top: string; height: string }
  onDeleteShift?: (shiftId: string) => void
  isDeleting: boolean
  isMobile: boolean
  isEditable?: boolean
}) {
  const formatTime = (dateTime: dayjs.Dayjs) => {
    return dateTime.format('HH:mm')
  }

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(shift.id.toString())
  }

  const getShiftColor = (driverName: string | null, isOverlapping: boolean) => {
    if (isOverlapping) return 'red'
    if (!driverName) return 'gray'
    return 'blue'
  }

  return (
    <Tooltip label={`${shift.driver?.name || 'No driver'} (ID: ${shift.id})`} withArrow position="top">
      <Paper
        p={isMobile ? 'sm' : 'md'}
        withBorder
        shadow="sm"
        style={{
          position: 'absolute',
          left: isMobile ? '2%' : '3%',
          right: isMobile ? '2%' : '3%',
          top: position.top,
          height: position.height,
          // In the Paper style:
          backgroundColor: `var(--mantine-color-${getShiftColor(shift.driver?.name || null, shift.isOverlapping)}-1)`,
          borderLeft: `4px solid var(--mantine-color-${getShiftColor(shift.driver?.name || null, shift.isOverlapping)}-6)`,
          // Add striped pattern for overlapping shifts
          backgroundImage:
            shift.isOverlapping && shift.overlapPosition === 'top'
              ? 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(0,0,0,0.1) 5px, rgba(0,0,0,0.1) 10px)'
              : 'none',

          zIndex: shift.isOverlapping && shift.overlapPosition === 'top' ? 3 : 1,

          overflow: 'hidden',

          minHeight: isMobile ? '35px' : '45px',
          cursor: 'pointer',
        }}
        onClick={handleCopyToClipboard}
      >
        <Stack gap={isMobile ? 4 : 6} style={{ height: '100%' }}>
          <Group justify="space-between" align="flex-start" gap="xs">
            <Text size="xs" fw={500} c="dimmed" style={{ fontSize: isMobile ? '11px' : '13px' }}>
              {formatTime(shift.startDateTime)} - {formatTime(shift.endDateTime)}
            </Text>
          </Group>
          <DriverNameDisplay driverName={shift.driver?.name || null} isMobile={isMobile} />
        </Stack>
        {onDeleteShift && isEditable && (
          <Box style={{ position: 'absolute', bottom: 4, right: 4, zIndex: 10 }}>
            <ActionIcon
              variant="subtle"
              color="black"
              size="xs"
              onClick={e => {
                e.stopPropagation()
                onDeleteShift(shift.id.toString())
              }}
              loading={isDeleting}
              disabled={isDeleting}
            >
              <TbTrash size={12} />
            </ActionIcon>
          </Box>
        )}
      </Paper>
    </Tooltip>
  )
}

// Reusable component for a timeline section
function TimelineSection({
  vehicleShifts,
  channels,
  getPosition,
  onDeleteShift,
  isDeleting,
  isMobile,
  timeSlots,
  isEditable,
  isContinuation,
}: {
  vehicleShifts: ExtendedVehicleShifts[]
  channels: string[]
  getPosition: (startDateTime: dayjs.Dayjs, endDateTime: dayjs.Dayjs) => { top: string; height: string }
  onDeleteShift?: (shiftId: string) => void
  isDeleting: boolean
  isMobile: boolean
  timeSlots: TimeSlot[]
  isEditable?: boolean
  isContinuation?: boolean
}) {
  return (
    <Box
      style={{
        position: 'relative',
        height: isMobile ? '450px' : '650px',
        marginLeft: isMobile ? '60px' : '80px',
        marginBottom: '40px',
      }}
    >
      {/* Time grid lines */}
      <TimeGrid timeSlots={timeSlots} isContinuation={isContinuation} />

      {/* Time labels on the left */}
      <TimeLabels timeSlots={timeSlots} isMobile={isMobile} isContinuation={isContinuation} />

      {/* Shifts */}
      {channels.map((channel, channelIndex) => {
        const vehicleData = vehicleShifts.find(vs => vs.vehicle_id === channel)
        const channelShifts = vehicleData?.shifts || []
        const channelWidth = 100 / channels.length
        const channelLeft = channelIndex * channelWidth + channelWidth * 0.05
        const channelRight = (channelIndex + 1) * channelWidth - channelWidth * 0.05

        return (
          <Box
            key={channel}
            style={{
              position: 'absolute',
              left: `${channelLeft}%`,
              right: `${100 - channelRight}%`,
              top: 0,
              bottom: 0,
              zIndex: 2,
            }}
          >
            {/* Shifts for this channel */}
            {channelShifts.map(shift => {
              const position = getPosition(shift.startDateTime, shift.endDateTime)
              return (
                <ShiftItem
                  key={shift.id}
                  shift={shift}
                  position={position}
                  onDeleteShift={onDeleteShift}
                  isDeleting={isDeleting}
                  isMobile={isMobile}
                  isEditable={isEditable}
                />
              )
            })}
          </Box>
        )
      })}
    </Box>
  )
}

export function ShiftsTimeline({
  vehicleShifts,
  allChannels,
  onDeleteShift,
  isDeleting = false,
  isEditable = false,
  dayWindow,
  isContinuation,
}: {
  vehicleShifts: ExtendedVehicleShifts[]
  allChannels: string[]
  onDeleteShift?: (shiftId: string) => void
  isDeleting?: boolean
  isEditable?: boolean
  dayWindow: ShiftDayWindow
  isContinuation?: boolean
}) {
  const isMobile = useMediaQuery('(max-width: 768px)')

  return (
    <div>
      <Title order={3} mt="md" mb="sm" c="light-dark(#868e96, #c9c9c9)">
        {dayWindow.start.format('DD.MM')} - {dayWindow.end.format('DD.MM')}
      </Title>

      <Card shadow="sm" padding="xl" radius="md" withBorder style={{ marginTop: isContinuation ? '1rem' : '0px' }}>
        <TimelineSection
          vehicleShifts={vehicleShifts}
          channels={allChannels}
          getPosition={getShiftPosition(dayWindow)}
          timeSlots={getTimeSlots(dayWindow, getTimezone())}
          onDeleteShift={onDeleteShift}
          isDeleting={isDeleting}
          isMobile={isMobile}
          isEditable={isEditable}
          isContinuation={isContinuation}
        />
      </Card>
    </div>
  )
}
